import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { exportCSVFile, exportExcelFile, tableTitlePaddingLeft } from '@helpers';
import { Paper, Stack, Table } from '@esgian/esgianui';
import { useTheme } from '@hooks';
import TableActions from '@components/Tables/TableActions';
import {
  filterQueryResults,
  getDefaultTableFilters,
  getFilterCount,
  getTableFilterResults
} from '@components/Tables/TableV2/helpers';

const TableV2 = ({
  title = <div />,
  loading = false,
  columns = [],
  options = {},
  data = [],
  handleCustomDownload = undefined
}) => {
  const [displayData, setDisplayData] = useState(null);
  const { theme } = useTheme();
  const [tableFilters, setTableFilters] = useState({});

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    if (!columns.length) return;
    setTableFilters(getDefaultTableFilters(columns));
  }, [columns]);
  useEffect(() => {
    setDisplayData(data);
  }, [data]);
  useEffect(() => {
    applyFiltersAndQuery();
  }, [tableFilters, debouncedSearchQuery]);

  const filterColumns = useMemo(() => {
    return columns.filter(({ options }) => options.filter !== false).map(({ name }) => name);
  }, [columns]);

  const applyFiltersAndQuery = useCallback(() => {
    let filteredData = [...data];

    // Apply search filtering
    filteredData = filterQueryResults(filteredData, filterColumns, debouncedSearchQuery);

    // Apply table filters
    filteredData = getTableFilterResults(filteredData, tableFilters);

    setDisplayData(filteredData);
  }, [debouncedSearchQuery, data, tableFilters]);

  const filterCount = useMemo(() => {
    return getFilterCount(tableFilters);
  }, [tableFilters]);

  const visibleColumns = columns.filter((col) => col.options?.display !== 'false');

  const handleExportClick = (type) => {
    if (type === 'CSV') {
      exportCSVFile(columns, displayData, options?.fileName ?? 'download');
    }
    if (type === 'Excel') {
      exportExcelFile(columns, displayData, options?.fileName ?? 'download');
    }
  };

  return (
    <Paper>
      <Stack>
        {(!options.hideToolbar || !options.hideTitle) && (
          <Stack
            alignItems={'center'}
            spacing={2}
            sx={{ p: 2 }}
            direction={'row'}
            justifyContent={'space-between'}>
            {!options.hideTitle && title}
            {!options.hideToolbar && (
              <TableActions
                setDebouncedSearchQuery={setDebouncedSearchQuery}
                setTableFilters={setTableFilters}
                tableFilters={tableFilters}
                loading={loading}
                columns={visibleColumns}
                originalData={data}
                handleCustomDownload={handleCustomDownload}
                handleExportClick={handleExportClick}
                filterCount={filterCount}
                hideFilter={options.hideFilter}
              />
            )}
          </Stack>
        )}
        <Table
          customStyle={{
            ...tableTitlePaddingLeft,
            MUIDataTableFilterList: { display: 'none' },
            MUIDataTableToolbar: {
              styleOverrides: {
                actions: {
                  display: 'none'
                }
              }
            }
          }}
          skeletonRows={6}
          loading={loading ?? displayData === null}
          mode={theme.mode}
          data={displayData ?? []}
          columns={visibleColumns}
          options={options}
        />
      </Stack>
    </Paper>
  );
};

TableV2.propTypes = {
  title: PropTypes.node,
  handleCustomDownload: PropTypes.func,
  loading: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.object),
  options: PropTypes.object,
  data: PropTypes.arrayOf(PropTypes.object)
};

export default TableV2;
